import { SelectOutlined } from "@ant-design/icons";
import { Button, Flex } from "antd";
import { FC, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TaskRoute } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { BaseModal } from "@/components/BaseModal";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { selectUncompletedTasks } from "@/store/slices/progress";
import { clearReminderModals } from "@/store/slices/ui";

import { StyledTaskCard } from "./Styled";

export interface UncompletedTaskModalProps {
    onCloseModal: () => void;
}

export const UncompletedTaskModal: FC<UncompletedTaskModalProps> = ({
    onCloseModal,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const uncompletedTasks = useAppSelector(selectUncompletedTasks);
    const { tasks, markets } = useAppSelector((s) => s.work);

    const availableTasks = useMemo(() => {
        return uncompletedTasks
            .map(({ taskId, marketId }) => {
                const currentTask = tasks[marketId]?.[taskId];
                const currentMarket = markets[marketId];

                if (!currentTask || !currentMarket) return null;

                return {
                    taskId,
                    marketId,
                    taskRoute: currentTask.context.route,
                    taskName: currentTask.name,
                    marketName: currentMarket.name,
                };
            })
            .filter(Boolean);
    }, [uncompletedTasks, tasks, markets]);

    const handleNavigateToTask = ({
        marketId,
        taskId,
        taskRoute,
    }: {
        taskId: string;
        marketId: string;
        taskRoute: TaskRoute;
    }) => {
        navigate(`${ROUTES.WorkPlan}/${marketId}/${taskRoute}/${taskId}`);

        dispatch(clearReminderModals());
    };

    const handleClose = () => {
        onCloseModal();
    };

    const handleIgnore = () => {
        onCloseModal();
    };

    useEffect(() => {
        if (availableTasks.length === 0) {
            handleClose();
        }
    }, [availableTasks.length]);

    if (availableTasks.length === 0) return null;

    return (
        <BaseModal
            open
            onClose={handleClose}
            actions={null}
            maskClosable={false}
            title={"У вас є незавершені задачі"}
        >
            <Flex vertical gap={16}>
                <Flex vertical gap={12}>
                    {availableTasks.map(
                        ({
                            taskId,
                            marketId,
                            taskName,
                            taskRoute,
                            marketName,
                        }) => {
                            return (
                                <StyledTaskCard
                                    key={taskId}
                                    align="center"
                                    justify="space-between"
                                    gap={8}
                                >
                                    <Flex vertical gap={4}>
                                        <PrimaryText fontWeight="500">
                                            {taskName}
                                        </PrimaryText>
                                        <SecondaryText fontSize={13}>
                                            {marketName}
                                        </SecondaryText>
                                    </Flex>

                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<SelectOutlined />}
                                        onClick={() =>
                                            handleNavigateToTask({
                                                taskId,
                                                marketId,
                                                taskRoute,
                                            })
                                        }
                                        style={{ flexShrink: 0 }}
                                    />
                                </StyledTaskCard>
                            );
                        },
                    )}
                </Flex>

                <BaseButton type="default" onClick={handleIgnore}>
                    Ігнорувати
                </BaseButton>
            </Flex>
        </BaseModal>
    );
};
